'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/authContext';
import AppLogo from '@/components/ui/AppLogo';
import { Eye, EyeOff, Mail, Lock, ArrowRight, Sparkles, GraduationCap, Shield, Building2 } from 'lucide-react';

const demoAccounts = [
  { label: 'Student', email: 'student@demo.com', icon: <GraduationCap size={14} />, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  { label: 'Super Admin', email: 'admin@demo.com', icon: <Shield size={14} />, color: 'bg-purple-50 text-purple-700 border-purple-200' },
  { label: 'College Manager', email: 'manager@demo.com', icon: <Building2 size={14} />, color: 'bg-green-50 text-green-700 border-green-200' },
];

export default function SignInPage() {
  const { signIn, profile } = useAuth();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const res = await signIn(email, password);
    setLoading(false);
    if (res.error) { setError(res.error); return; }
    // Role-based redirect
    const role = res.role;
    if (role === 'super_admin') router.push('/admin/super');
    else if (role === 'college_manager') router.push('/admin/college-manager');
    else router.push('/student-profile');
  };

  const fillDemo = (demoEmail: string) => {
    setEmail(demoEmail);
    setPassword('demo1234');
    setError('');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-secondary p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-primary blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-accent blur-3xl" />
        </div>
        <Link href="/home" className="flex items-center gap-2 relative z-10">
          <AppLogo size={40} />
          <span className="font-extrabold text-2xl text-white">Edu<span className="text-primary" style={{color:'#A78BFA'}}>Portal</span></span>
        </Link>
        <div className="relative z-10">
          <h2 className="text-4xl font-extrabold text-white leading-tight mb-4">
            India's Smartest<br />College Discovery<br />Platform
          </h2>
          <p className="text-white/70 text-lg mb-8">25,000+ colleges. AI-powered matching. Real-time admission predictions.</p>
          <div className="space-y-3">
            {['Access your personalised college shortlist', 'Track application status in real-time', 'Chat with AI career advisor anytime'].map(f => (
              <div key={f} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-primary/30 flex items-center justify-center shrink-0">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
                <span className="text-white/80 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/40 text-xs relative z-10">© 2026 All About Campus. All rights reserved.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link href="/home" className="lg:hidden flex items-center gap-2 mb-8">
            <AppLogo size={32} />
            <span className="font-extrabold text-xl text-secondary">Edu<span className="text-primary">Portal</span></span>
          </Link>

          <div className="mb-8">
            <h1 className="text-3xl font-extrabold text-foreground mb-2">Welcome back</h1>
            <p className="text-muted-foreground">Sign in to your All About Campus account</p>
          </div>

          {/* Demo accounts */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Quick Demo Login</p>
            <div className="flex gap-2 flex-wrap">
              {demoAccounts.map(d => (
                <button key={d.email} onClick={() => fillDemo(d.email)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-all hover:scale-105 ${d.color}`}>
                  {d.icon}{d.label}
                </button>
              ))}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  placeholder="you@example.com"
                  className="input-base pl-9" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-foreground">Password</label>
                <button type="button" className="text-xs text-primary font-semibold hover:underline">Forgot password?</button>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type={showPass ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required
                  placeholder="Enter your password"
                  className="input-base pl-9 pr-10" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">{error}</div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base">
              {loading ? (
                <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Signing in...</span>
              ) : (
                <span className="flex items-center gap-2">Sign In <ArrowRight size={16} /></span>
              )}
            </button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Don't have an account?{' '}
            <Link href="/sign-up" className="text-primary font-semibold hover:underline">Create one free</Link>
          </p>

          <div className="mt-6 flex items-center gap-2 bg-accent/50 rounded-xl p-3">
            <Sparkles size={16} className="text-primary shrink-0" />
            <p className="text-xs text-muted-foreground">Use demo credentials above to explore all roles. Password: <strong>demo1234</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
}
